import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL, NGROK_URL } from "../../utils/const";
import "./Division7aCalculator.css";

const formSchema = {
  decisionTool: {
    fields: [
      {
        type: "radio",
        name: "decisionType",
        label: "Select one of the following options *",
        required: true,
        options: [
          { 
            value: "loanDividend", 
            label: "Determine whether a loan from a private company to a shareholder or shareholder's associate will be deemed a dividend" 
          },
          { 
            value: "paymentDividend", 
            label: "Determine whether a payment from a private company to a shareholder or shareholder's associate will be deemed a dividend" 
          },
          { 
            value: "debtForgiven", 
            label: "Determine whether a debt owed by a shareholder or shareholder's associate to a private company has been forgiven and will be deemed a dividend" 
          },
          { 
            value: "calculateRepayment", 
            label: "Calculate a minimum yearly repayment and the amount of the loan not repaid by the end of an income year" 
          },
        ],
      },
    ],
  },
  incomeYearOfLoan: {
    condition: (formData) => formData.decisionType === "loanDividend",
    fields: [
      {
        type: "select",
        name: "incomeYearOfLoan",
        label: "Income year of loan *",
        required: true,
        options: [
          { value: "2012-13", label: "2012-13" },
          { value: "2013-14", label: "2013-14" },
          { value: "2014-15", label: "2014-15" },
          { value: "2015-16", label: "2015-16" },
          { value: "2016-17", label: "2016-17" },
          { value: "2017-18", label: "2017-18" },
          { value: "2018-19", label: "2018-19" },
          { value: "2019-20", label: "2019-20" },
          { value: "2020-21", label: "2020-21" },
          { value: "2021-22", label: "2021-22" },
          { value: "2022-23", label: "2022-23" },
          { value: "2023-24", label: "2023-24" },
        ],
      },
    ],
  },
  divisionYearApplies: {
    condition: (formData) => formData.incomeYearOfLoan && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "select",
        name: "divisionYearApplies",
        label: "Income year that you are enquiring as to whether Division 7A applies *",
        required: true,
        options: (formData) => {
          const allYears = [
            { value: "2012-13", label: "2012-13" },
            { value: "2013-14", label: "2013-14" },
            { value: "2014-15", label: "2014-15" },
            { value: "2015-16", label: "2015-16" },
            { value: "2016-17", label: "2016-17" },
            { value: "2017-18", label: "2017-18" },
            { value: "2018-19", label: "2018-19" },
            { value: "2019-20", label: "2019-20" },
            { value: "2020-21", label: "2020-21" },
            { value: "2021-22", label: "2021-22" },
            { value: "2022-23", label: "2022-23" },
            { value: "2023-24", label: "2023-24" },
          ];
          if (!formData.incomeYearOfLoan) {
            return allYears;
          }
          const selectedLoanYearStart = parseInt(formData.incomeYearOfLoan.substring(0, 4), 10);
          return allYears.filter(yearOption => {
            const optionYearStart = parseInt(yearOption.value.substring(0, 4), 10);
            return optionYearStart >= selectedLoanYearStart;
          });
        },
      },
    ],
  },
  fullyRepaidByLodgmentDay: {
    condition: (formData) => formData.divisionYearApplies && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "fullyRepaidByLodgmentDay",
        label: "Was the loan (or will this loan be) fully repaid by the lodgment day? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  duringWindingUp: {
    condition: (formData) => formData.fullyRepaidByLodgmentDay==='no' && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "duringWindingUp",
        label: "Was the loan made during the course of winding-up the private company? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  ordinaryCourseOfBusiness: {
    condition: (formData) => formData.duringWindingUp==='no' && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "ordinaryCourseOfBusiness",
        label: "Was the loan made in the ordinary course of the private company's business and on the usual terms on which the private company makes similar loans to parties at arm's length? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  acquireQualifyingShares: {
    condition: (formData) => formData.ordinaryCourseOfBusiness==='no' && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "acquireQualifyingShares",
        label: "Was the loan made solely for the purpose of enabling the shareholder or shareholder's associate to acquire qualifying shares? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  includedAsAssessableIncome: {
    condition: (formData) => (formData.acquireQualifyingShares==='no' && formData.decisionType === "loanDividend"),
    fields: [
      {
        type: "radio",
        name: "includedAsAssessableIncome",
        label: "Was the loan included as assessable income by the shareholder or shareholder's associate? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  excludedFromAssessableIncome: {
    condition: (formData) => formData.includedAsAssessableIncome==='no' && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "excludedFromAssessableIncome",
        label: "Was the loan specifically excluded from the assessable income by the shareholder or shareholder's associate? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  writtenLoanAgreement: {
    condition: (formData) => formData.excludedFromAssessableIncome==='no' && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "writtenLoanAgreement",
        label: "Was the loan made under a written loan agreement? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  agreementBeforeLodgmentDay: {
    condition: (formData) => formData.writtenLoanAgreement && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "agreementBeforeLodgmentDay",
        label: "Was the agreement in place before the lodgment day for income year 2012-13? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  interestRateExceedsBenchmark: {
    condition: (formData) => formData.agreementBeforeLodgmentDay && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "interestRateExceedsBenchmark",
        label: "Is the rate of interest payable in subsequent years to income year 2012-13 equal to or in excess of the benchmark interest rate? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  termWithinAllowable: {
    condition: (formData) => formData.interestRateExceedsBenchmark && formData.decisionType === "loanDividend",
    fields: [
      {
        type: "radio",
        name: "termWithinAllowable",
        label: "Is the term of the loan less than or equal to the maximum term allowable for that type of loan? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  
  // Option 2: Payment Dividend
  paymentAsEmployee: {
    condition: (formData) => formData.decisionType === "paymentDividend",
    fields: [
      {
        type: "radio",
        name: "paymentAsEmployee",
        label: "Was the payment made to a shareholder or shareholder's associate in their capacity as an employee or as associate of such an employee? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  paymentAgainstGenuineDebt: {
    condition: (formData) => formData.paymentAsEmployee && formData.decisionType === "paymentDividend",
    fields: [
      {
        type: "radio",
        name: "paymentAgainstGenuineDebt",
        label: "Was the payment made against a genuine debt owed by a private company to a shareholder or the shareholder's associate? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  paymentIncludedAsAssessable: {
    condition: (formData) => formData.paymentAgainstGenuineDebt && formData.decisionType === "paymentDividend",
    fields: [
      {
        type: "radio",
        name: "paymentIncludedAsAssessable",
        label: "Was the payment included as assessable income by the shareholder or shareholder's associate? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  paymentExcludedFromAssessable: {
    condition: (formData) => formData.paymentIncludedAsAssessable && formData.decisionType === "paymentDividend",
    fields: [
      {
        type: "radio",
        name: "paymentExcludedFromAssessable",
        label: "Was the payment specifically excluded from the assessable income by the shareholder or shareholder's associate? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  distributionDuringWindingUp: {
    condition: (formData) => formData.paymentExcludedFromAssessable && formData.decisionType === "paymentDividend",
    fields: [
      {
        type: "radio",
        name: "distributionDuringWindingUp",
        label: "Was there a distribution made during the course of winding-up the private company? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  paymentToBeConvertedToLoan: {
    condition: (formData) => formData.distributionDuringWindingUp && formData.decisionType === "paymentDividend",
    fields: [
      {
        type: "radio",
        name: "paymentToBeConvertedToLoan",
        label: "Is the payment to be converted to a loan? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },

  // Option 3: Debt Forgiven
  debtObligationReleased: {
    condition: (formData) => formData.decisionType === "debtForgiven",
    fields: [
      {
        type: "radio",
        name: "debtObligationReleased",
        label: "Was the obligation to pay the debt or part of the debt by the shareholder or shareholder's associate released, waived or otherwise extinguished? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  futureObligationCeaseAgreement: {
    condition: (formData) => formData.debtObligationReleased && formData.decisionType === "debtForgiven",
    fields: [
      {
        type: "radio",
        name: "futureObligationCeaseAgreement",
        label: "Did the shareholder or shareholder's associate enter into an agreement with the private company where the obligation to pay the whole or part of the debt would cease at a future time, without having to incur any financial or other obligation? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  sharesSubscribedToDischargeDebt: {
    condition: (formData) => formData.futureObligationCeaseAgreement && formData.decisionType === "debtForgiven",
    fields: [
      {
        type: "radio",
        name: "sharesSubscribedToDischargeDebt",
        label: "Did the shareholder or shareholder's associate subscribe for shares in the private company to enable the company to make a payment in discharge of the debt and the company applied some or all of the subscription money towards the debt? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  debtParkingArrangement: {
    condition: (formData) => formData.sharesSubscribedToDischargeDebt && formData.decisionType === "debtForgiven",
    fields: [
      {
        type: "radio",
        name: "debtParkingArrangement",
        label: "Did the shareholder or shareholder's associate enter into a debt parking arrangement? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  companyNoLongerInsisting: {
    condition: (formData) => formData.debtParkingArrangement && formData.decisionType === "debtForgiven",
    fields: [
      {
        type: "radio",
        name: "companyNoLongerInsisting",
        label: "Is the private company no longer insisting or relying on the shareholder or shareholder's associate meeting the obligation to repay the debt? *",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  
  // Option 4: Calculate Repayment
  repaymentLoanYear: {
    condition: (formData) => formData.decisionType === "calculateRepayment",
    fields: [
      {
        type: "select",
        name: "repaymentLoanYear",
        label: "Income year of loan *",
        required: true,
        options: [
          { value: "2012-13", label: "2012-13" },
          { value: "2013-14", label: "2013-14" },
          { value: "2014-15", label: "2014-15" },
          { value: "2015-16", label: "2015-16" },
          { value: "2016-17", label: "2016-17" },
          { value: "2017-18", label: "2017-18" },
          { value: "2018-19", label: "2018-19" },
          { value: "2019-20", label: "2019-20" },
          { value: "2020-21", label: "2020-21" },
          { value: "2021-22", label: "2021-22" },
          { value: "2022-23", label: "2022-23" },
          { value: "2023-24", label: "2023-24" },
        ],
      },
    ],
  },
  loanSecurityType: {
    condition: (formData) => formData.repaymentLoanYear && formData.decisionType === "calculateRepayment",
    fields: [
      {
        type: "radio",
        name: "loanSecurityType",
        label: "Is the loan secured or unsecured? *",
        required: true,
        options: [
          { value: "secured", label: "Secured loan" },
          { value: "unsecured", label: "Unsecured loan" },
        ],
      },
    ],
  },
  amalgamatedLoanTerm: {
    condition: (formData) => formData.loanSecurityType && formData.decisionType === "calculateRepayment",
    fields: [
      {
        type: "number",
        name: "amalgamatedLoanTerm",
        label: "Full term of the amalgamated loan in years *",
        required: true,
        min: 1,
        max: 25,
      },
    ],
  },
  calculationYear: {
    condition: (formData) => formData.amalgamatedLoanTerm && formData.decisionType === "calculateRepayment",
    fields: [
      {
        type: "select",
        name: "calculationYear",
        label: "Income year that you wish to calculate the minimum yearly repayment *",
        required: true,
        options: [
          { value: "2012-13", label: "2012-13" },
          { value: "2013-14", label: "2013-14" },
          { value: "2014-15", label: "2014-15" },
          { value: "2015-16", label: "2015-16" },
          { value: "2016-17", label: "2016-17" },
          { value: "2017-18", label: "2017-18" },
          { value: "2018-19", label: "2018-19" },
          { value: "2019-20", label: "2019-20" },
          { value: "2020-21", label: "2020-21" },
          { value: "2021-22", label: "2021-22" },
          { value: "2022-23", label: "2022-23" },
          { value: "2023-24", label: "2023-24" },
          { value: "2024-25", label: "2024-25" },
        ],
      },
    ],
  },
  unpaidLoanAmount: {
    condition: (formData) => formData.calculationYear && formData.decisionType === "calculateRepayment",
    fields: [
      {
        type: "currency",
        name: "unpaidLoanAmount",
        label: "Amount of the amalgamated loan not repaid by the end of the 2023-24 income year *",
        required: true,
        // prefix: "$",
      },
    ],
  },
  calculateComponents: {
    condition: (formData) => formData.unpaidLoanAmount && formData.decisionType === "calculateRepayment",
    fields: [
      {
        type: "radio",
        name: "calculateComponents",
        label: (formData) => `Would you like to calculate the principal and interest component of repayments, and the amount of the amalgamated loan not repaid by the end of the ${formData.calculationYear} income year? *`,
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
};

const Decision7ACalculator = () => {
  const [formData, setFormData] = useState({
    decisionType: "",
    incomeYearOfLoan: "",
    divisionYearApplies: "",
    fullyRepaidByLodgmentDay: "",
    duringWindingUp: "",
    ordinaryCourseOfBusiness: "",
    acquireQualifyingShares: "",
    includedAsAssessableIncome: "",
    excludedFromAssessableIncome: "",
    writtenLoanAgreement: "",
    agreementBeforeLodgmentDay: "",
    interestRateExceedsBenchmark: "",
    termWithinAllowable: "",
    paymentAsEmployee: "",
    paymentAgainstGenuineDebt: "",
    paymentIncludedAsAssessable: "",
    paymentExcludedFromAssessable: "",
    distributionDuringWindingUp: "",
    paymentToBeConvertedToLoan: "",
    debtObligationReleased: "",
    futureObligationCeaseAgreement: "",
    sharesSubscribedToDischargeDebt: "",
    debtParkingArrangement: "",
    companyNoLongerInsisting: "",
    repaymentLoanYear: "",
    loanSecurityType: "",
    amalgamatedLoanTerm: "",
    calculationYear: "",
    unpaidLoanAmount: "",
    calculateComponents: "",
  });
  
  const [repayments, setRepayments] = useState([]);
  const [currentRepayment, setCurrentRepayment] = useState({
    date: "",
    amount: "",
  });
  const [editingRepaymentId, setEditingRepaymentId] = useState(null);
  const [expandedRepaymentId, setExpandedRepaymentId] = useState(null);
  const [showRepaymentForm, setShowRepaymentForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleRepaymentChange = (e) => {
    const { name, value } = e.target;
    setCurrentRepayment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddRepayment = () => {
    setCurrentRepayment({ date: "", amount: "" });
    setEditingRepaymentId(null);
    setShowRepaymentForm(true);
  };

  const handleSaveRepayment = () => {
    if (currentRepayment.date && currentRepayment.amount) {
      if (editingRepaymentId) {
        // Update existing repayment
        setRepayments(
          repayments.map((repayment) =>
            repayment.id === editingRepaymentId
              ? { ...currentRepayment, id: editingRepaymentId }
              : repayment
          )
        );
        setEditingRepaymentId(null);
      } else {
        // Add new repayment
        setRepayments([...repayments, { ...currentRepayment, id: Date.now() }]);
      }
      setCurrentRepayment({ date: "", amount: "" });
      setShowRepaymentForm(false);
    }
  };

  const handleCancelRepayment = () => {
    setCurrentRepayment({ date: "", amount: "" });
    setEditingRepaymentId(null);
    setShowRepaymentForm(false);
  };

  const handleEditRepayment = (repayment) => {
    setCurrentRepayment({
      date: repayment.date,
      amount: repayment.amount,
    });
    setEditingRepaymentId(repayment.id);
    setShowRepaymentForm(true);
  };

  const handleDeleteRepayment = (id) => {
    setRepayments(repayments.filter((repayment) => repayment.id !== id));
    if (editingRepaymentId === id) {
      setEditingRepaymentId(null);
      setCurrentRepayment({ date: "", amount: "" });
      setShowRepaymentForm(false);
    }
  };

  const toggleAccordion = (id) => {
    setExpandedRepaymentId(expandedRepaymentId === id ? null : id);
  };

  const renderField = (field) => {
    if (field.condition && !field.condition(formData)) return null;

    switch (field.type) {
      case "select":
        const currentOptions = typeof field.options === 'function' ? field.options(formData) : field.options;
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <select
              id={field.name}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              required={field.required}
            >
              <option value="">Select</option>
              {currentOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );

      case "radio":
        return (
          <div key={field.name} className="form-group">
            <label>
              {typeof field.label === "function"
                ? field.label(formData)
                : field.label}
            </label>
            {field.subLabel && (
              <ul className="sub-label">
                {field.subLabel.map((text, index) => (
                  <li key={index}>{text}</li>
                ))}
              </ul>
            )}
            <div className="radio-group">
              {field.options.map((opt) => (
                <div key={opt.value} className="radio-option">
                  <input
                    type="radio"
                    id={`${field.name}-${opt.value}`}
                    name={field.name}
                    value={opt.value}
                    checked={formData[field.name] === opt.value}
                    onChange={handleChange}
                    required={field.required}
                  />
                  <label htmlFor={`${field.name}-${opt.value}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
            {field.tooltip && (
              <div className="info-tooltip">
                <div className="info-icon">i</div>
                <div className="tooltip-text">{field.tooltip}</div>
              </div>
            )}
          </div>
        );

      case "currency":
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <div className="input-with-prefix">
              <span className="currency-symbol">{field.prefix}</span>
              <input
                type="number"
                id={field.name}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.required}
                step="0.01"
              />
            </div>
          </div>
        );

      case "number":
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <div className="input-with-suffix">
              <input
                type="number"
                id={field.name}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.required}
                min={field.min}
                max={field.max}
              />
              {field.suffix && <span className="unit">{field.suffix}</span>}
            </div>
            {field.tooltip && (
              <div className="info-tooltip">
                <div className="info-icon">i</div>
                <div className="tooltip-text">{field.tooltip}</div>
              </div>
            )}
          </div>
        );

      default:
        return <></>;
    }
  };

  const renderRepaymentForm = () => {
    if (!showRepaymentForm || formData.calculateComponents !== "yes") return null;

    return (
      <div className="repayment-form">
        <h3>Loan repayment details</h3>
        <div className="form-group">
          <label htmlFor="date">Date loan repayment made *</label>
          <div className="date-input-container">
            <input
              type="date"
              id="date"
              name="date"
              value={currentRepayment.date}
              onChange={handleRepaymentChange}
              required
              placeholder="dd/mm/yyyy"
            />
            <button className="calendar-button" type="button">
              <span className="calendar-icon">📅</span>
            </button>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="amount">Amount of repayment *</label>
          <div className="input">
            {/* <span className="">$</span> */}
            <input
              type="number"
              id="amount"
              name="amount"
              value={currentRepayment.amount}
              onChange={handleRepaymentChange}
              required
              step="0.01"
            />
          </div>
        </div>
        <div className="button-group">
          {editingRepaymentId && (
            <button
              type="button"
              className="btn btn-delete"
              onClick={() => handleDeleteRepayment(editingRepaymentId)}
            >
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancelRepayment}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveRepayment}
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  const renderRepaymentsList = () => {
    if (repayments.length === 0 || formData.calculateComponents !== "yes") return null;

    return (
      <div className="repayments-list">
        {repayments.map((repayment) => (
          <div key={repayment.id} className="repayment-item">
            <div 
              className="repayment-header" 
              onClick={() => toggleAccordion(repayment.id)}
            >
              <div className="repayment-date">{repayment.date}</div>
              <div className="repayment-amount">${parseFloat(repayment.amount).toFixed(2)}</div>
              <button className="accordion-toggle" type="button">
                <span className={`accordion-icon ${expandedRepaymentId === repayment.id ? "expanded" : ""}`}>
                  {expandedRepaymentId === repayment.id ? "▲" : "▼"}
                </span>
              </button>
            </div>

            {expandedRepaymentId === repayment.id && (
              <div className="repayment-details">
                <h3>Loan repayment details</h3>
                <div className="form-group">
                  <label>Date loan repayment made *</label>
                  <div className="date-input-container">
                    <input
                      type="date"
                      value={repayment.date}
                      readOnly
                    />
                    <button className="calendar-button" type="button">
                      <span className="calendar-icon">📅</span>
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Amount of repayment *</label>
                  <div className="input-with-prefix">
                    <span className="currency-symbol">$</span>
                    <input
                      type="text"
                      value={parseFloat(repayment.amount).toFixed(2)}
                      readOnly
                    />
                  </div>
                </div>
                <div className="button-group">
                  <button
                    type="button"
                    className="btn btn-delete"
                    onClick={() => handleDeleteRepayment(repayment.id)}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => toggleAccordion(repayment.id)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleEditRepayment(repayment)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderRepaymentsSection = () => {
    if (formData.calculateComponents !== "yes") return null;

    return (
      <div className="repayments-section">
        <div className="repayments-header">
          <h3>Add repayments made during the {formData.calculationYear} income year</h3>
          <button type="button" className="btn btn-add" onClick={handleAddRepayment}>
            Add
          </button>
        </div>
        {showRepaymentForm && renderRepaymentForm()}
        {renderRepaymentsList()}
      </div>
    );
  };

  const renderSection = (section, sectionKey) => {
    if (section.condition && !section.condition(formData)) return null;

    return (
      <div key={sectionKey} className="form-section">
        {section.title && <h2>{section.title}</h2>}
        {section.fields.map((field) => renderField(field))}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Map form data to the expected API payload format based on the selected calculator option
      let apiPayload = {};
      
      // Helper function to convert radio button values to "yes" or "no"
      const toYesNo = (value) => value === "yes" ? "yes" : "no";

      // Helper function to extract year from "YYYY-YY" or "YYYY" format
      const extractYear = (yearRange) => {
        if (!yearRange) return '';
        const match = yearRange.match(/^(\d{4})/); // Extracts the first YYYY
        return match ? match[1] : '';
      };
      
      if (formData.decisionType === "calculateRepayment") {
        // Option 4: Calculate minimum yearly repayment
        let formattedRepayments = [];
        
        if (formData.calculateComponents === "yes" && repayments.length > 0) {
          formattedRepayments = repayments.map(r => {
            try {
              return {
                date: formatDate(r.date),
                amount: parseFloat(r.amount || 0).toFixed(2)
              };
            } catch (error) {
              console.error("Error formatting repayment:", error);
              return { date: '', amount: '0.00' };
            }
          }).filter(r => r.date && r.amount !== '0.00');
        }
        
        apiPayload = {
          tool_option: "calculate",
          calc_income_year_of_loan: extractYear(formData.repaymentLoanYear),
          calc_loan_type: formData.loanSecurityType || "unsecured", 
          calc_full_term_loan_years: formData.amalgamatedLoanTerm || "7",
          calc_income_year_of_enquiry: extractYear(formData.calculationYear), 
          calc_loan_not_paid_by_eofy: formData.unpaidLoanAmount 
            ? parseFloat(formData.unpaidLoanAmount).toFixed(2) 
            : null,
          calc_calculate_amount_components: toYesNo(formData.calculateComponents),
          calc_lodgment_date:null,
          calc_repayments: formattedRepayments,
          debug_pause: 0
        };
      } else if (formData.decisionType === "loanDividend") {
        // Option 1: Determine whether a loan will be deemed a dividend
        apiPayload = {
          tool_option: "loan",
          income_year_loan: extractYear(formData.incomeYearOfLoan),
          income_year_enquiry: extractYear(formData.divisionYearApplies),
          repaid_by_lodgment: toYesNo(formData.fullyRepaidByLodgmentDay),
          winding_up_company: toYesNo(formData.duringWindingUp),
          loan_made_in_ordinary_course: toYesNo(formData.ordinaryCourseOfBusiness),
          loan_to_acquire_qualifying_shares: toYesNo(formData.acquireQualifyingShares),
          loan_included_as_assessable_income: toYesNo(formData.includedAsAssessableIncome),
          loan_excluded_as_assessable_income: toYesNo(formData.excludedFromAssessableIncome),
          written_loan_agreement: toYesNo(formData.writtenLoanAgreement),
          agreement_placement_date: toYesNo(formData.agreementBeforeLodgmentDay),
          rate_of_interest_payable: toYesNo(formData.interestRateExceedsBenchmark),
          term_of_loan: toYesNo(formData.termWithinAllowable),
          debug_pause: 0
        };
      } else if (formData.decisionType === "paymentDividend") {
        // Option 2: Determine whether a payment will be deemed a dividend
        apiPayload = {
          tool_option: "payment",
          payment_to_employee: toYesNo(formData.paymentAsEmployee),
          payment_made_against_genuine_debt: toYesNo(formData.paymentAgainstGenuineDebt),
          payment_included_as_assessable_income: toYesNo(formData.paymentIncludedAsAssessable),
          payment_excluded_as_assessable_income: toYesNo(formData.paymentExcludedFromAssessable),
          distribution_winding_up_company: toYesNo(formData.distributionDuringWindingUp),
          payment_convert_to_loan: toYesNo(formData.paymentToBeConvertedToLoan),
          debug_pause: 0
        };
      } else if (formData.decisionType === "debtForgiven") {
        // Option 3: Determine whether a debt has been forgiven
        apiPayload = {
          tool_option: "debt",
          waived_or_extinguished_debt: toYesNo(formData.debtObligationReleased),
          pay_debt_at_future_time: toYesNo(formData.futureObligationCeaseAgreement),
          subscription_money: toYesNo(formData.sharesSubscribedToDischargeDebt),
          debt_parking_agreement: toYesNo(formData.debtParkingArrangement),
          non_insisting_private_company: toYesNo(formData.companyNoLongerInsisting),
          debug_pause: 0
        };
      }

      console.log("Submitting payload:", apiPayload);
      
      const response = await axios.post(
        `${API_BASE_URL}/ato_calculator/division7a_calculator`,
        apiPayload,
        {timeout:Number.POSITIVE_INFINITY}
      );
      
      setResult(response?.data?.result_text);
    } catch (err) {
      console.error("API Error:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while processing your request"
      );
    } finally {
      setLoading(false);
    }
  };
   
  // Helper function to format dates in DD/MM/YYYY format
  const formatDate = (dateString) => {
    // If date is already in DD/MM/YYYY format, return it
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return ''; // Invalid date
      }
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return '';
    }
  };
   
  // // Helper function to generate lodgment date
  // const formatLodgmentDate = () => {
  //   try {
  //     // Otherwise, use a default lodgment date (e.g., May 15th of the calculationYear)
  //     // Extract the year from the calculation year (e.g., if "2023", use "2023")
  //     const yearMatch = formData.calculationYear?.match(/^(\d{4})/);
  //     const year = yearMatch ? yearMatch[1] : new Date().getFullYear();
      
  //     return `15/05/${year}`;
  //   } catch (error) {
  //     console.error("Error formatting lodgment date:", error);
  //     return '15/05/2024'; // Default fallback
  //   }
  // };

  return (
    <div className="form-container">
      <h1>Division 7A Calculator</h1>
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {Object.entries(formSchema).map(([key, section]) =>
          renderSection(section, key)
        )}
        
        {formData.decisionType === "calculateRepayment" && formData.calculateComponents === "yes" && 
          renderRepaymentsSection()
        }
        
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Calculating..." : "Calculate"}
        </button>
      </form>

      {loading && <div className="loader"></div>}

      {result && (
        <div className="result-container">
          <h2>Results</h2>
          <div className="result-content">
            <p>{result}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Decision7ACalculator;
